<template>
    <div>
        <div class="flex justify-between mb-8 items-center">
            <div>
                <h1 class="text-4xl tracking-widest uppercase font-bold text-gray-700">Latest Transactions</h1>
            </div>
            <div>
                <button class="btn-primary muted inline-block" type="button" @click="paymentLinks">Payment Links</button>
                <button class="btn-primary" type="button" @click="newSale">New Sale</button>
            </div>
        </div>

        <div class="flex flex-col mt-12">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Product
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Cost
                                </th>
                                <th scope="col" class="relative px-6 py-3">
                                    <span class="sr-only">Edit</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="invoice in invoices" :key="invoice.id">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <router-link :to="{ name: 'sales.view', params: { invoice: invoice.id }}">
                                    <div class="flex items-center">
                                        <div class="text-sm font-medium text-gray-900">
                                            <span>{{ invoice.customer_email }}</span>
                                        </div>
                                    </div>
                                    </router-link>
                                </td>

                                <td class="px-6 py-4 whitespace-nowrap">
                                    <router-link :to="{ name: 'sales.view', params: { invoice: invoice.id }}">
                                    <div class="text-sm font-medium text-gray-900">
                                    <span class="block" v-for="item in invoice.lines.data" :key="item.id">
                                        <ArrowPathIcon class="h-4 w-4 mr-2 text-gray-400 inline"
                                                     v-if="item.subscription"/> {{ item.description }}
                                    </span>
                                    </div>
                                    </router-link>
                                </td>

                                <td class="px-6 py-4 whitespace-nowrap">
                                    <router-link :to="{ name: 'sales.view', params: { invoice: invoice.id }}">
                                    <div class="flex items-center">
                                        <div class="text-sm text-gray-900">
                                            {{ currencyFormat(invoice.currency, invoice.total) }}
                                        </div>
                                        <span v-if="invoice.payment_intent?.charges?.data[0]?.amount_refunded > 0"
                                              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 ml-4">Refunded</span>
                                        <span v-else-if="['uncollectible'].includes(invoice.status)"
                                              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 ml-4">Lapsed</span>
                                        <span v-else-if="['void'].includes(invoice.status)"
                                              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 ml-4">Void</span>
                                        <span v-else-if="['open', 'draft'].includes(invoice.status)"
                                              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 ml-4">Open</span>
                                        <span v-else-if="['paid'].includes(invoice.status)"
                                              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 ml-4">Paid</span>
                                    </div>
                                    </router-link>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <a href="#" class="text-indigo-600 hover:text-indigo-900">
                                        <router-link :to="{ name: 'sales.view', params: { invoice: invoice.id }}">
                                            <EyeIcon class="h-5 w-5 text-gray-400 hover:text-gray-600"/>
                                        </router-link>
                                    </a>
                                </td>
                            </tr>
                            </tbody>
                            <tfoot>
                            <tr>
                                <td colspan="4">
                                    <nav
                                        class="w-full bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6"
                                        aria-label="Pagination">
                                        <div class="hidden sm:block">
                                            <p class="text-sm text-gray-700">
                                                Showing records
                                                {{ ' ' }}
                                                <span class="font-medium">1</span>
                                                {{ ' ' }}
                                                to
                                                {{ ' ' }}
                                                <span class="font-medium">{{ total }}</span>
                                            </p>
                                        </div>
                                        <div class="flex-1 flex justify-between sm:justify-end">
                                            <a v-if="seeMoreButton" @click.prevent="seeMore" href="#"
                                               class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">More</a>
                                        </div>
                                    </nav>
                                </td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <router-view v-slot="{ Component }">
            <component :is="Component"></component>
        </router-view>
    </div>
</template>

<script>

import {EyeIcon, ArrowPathIcon} from "@heroicons/vue/24/outline"
import {onMounted, ref} from "vue"
import {useRouter} from "vue-router"

export default {
    components: {
        ArrowPathIcon,
        EyeIcon,
    },
    beforeRouteUpdate(to, from, next) {
        if (from.name === 'sales.new' || from.name === 'sales.view') {
            this.getInvoices()
        }

        next()
    },
    setup() {
        const router = useRouter()
        const invoices = ref([])
        const seeMoreButton = ref(false)
        const total = ref(0)

        const getInvoices = async (lastItem = null) => {
            await axios.get(route('transactions.invoices', {from: lastItem?.id}))
                .then((response) => {
                    invoices.value = invoices.value.concat(response.data.data)
                    seeMoreButton.value = response.data?.has_more
                    total.value = total.value + invoices.value.length
                })
        }

        onMounted(() => {
            getInvoices()
        })

        const seeMore = () => {
            let lastItem = invoices.value[invoices.value.length - 1]

            getInvoices(lastItem)
        }

        const newSale = () => {
            router.push({ name: 'sales.new' })
        }

        const paymentLinks = () => {
            router.push({ name: 'payment.links' })
        }

        return {
            invoices,
            getInvoices,
            seeMore,
            seeMoreButton,
            total,
            newSale,
            paymentLinks,
        }
    },
}
</script>
